<template>
	<div v-if="visible" class="fd-always-visible" ref="avContainer" :style="containerStyle">
		<Teleport to="body" :disabled="!floating"><div class="fd-always-visible-floater" :class="floaterClass" :style="floaterStyle">
			<component :is="componentOfPart(bodyPart)" :part="partRef(bodyPart)" :data="localData" :readonly="ro"
					   :viewmode="vm"></component>
		</div></Teleport>
	</div>
</template>

<script setup lang="ts">
import useContainer, {type ContainerProps, containerProps} from "./Container";
import type {AlwaysVisible} from "@t/fd/layout/AlwaysVisible";
import {EMPTY_PART} from "../ComponentResolver";
import {computed, type CSSProperties, onMounted, ref} from "vue";
import {getElementMeasure} from "../helpers/DomUtils";
import {injectLayoutSize} from "@fd/LayoutWidth.ts";

const uprops = defineProps({...containerProps});
const props = uprops as ContainerProps;

const av = computed<AlwaysVisible>(() => props.part as AlwaysVisible);

const {visible, clazz, style, componentOfPart, partRef, localData, ro, vm} = useContainer(props);

const bodyPart = computed(() => {
	return av.value.body || EMPTY_PART;
});

const avContainer = ref<HTMLDivElement | null>(null);
const floating = ref<boolean>(false);
const screenSize = injectLayoutSize();

interface FloatMeasure {
	containerWidth: number;
	containerHeight: number;
	floaterLeft: number;
	floaterTop: number;
}

const measure = computed<FloatMeasure>(() => {
	const ms:FloatMeasure = {
		containerWidth:0, containerHeight:0, floaterLeft:0, floaterTop: 0,
	};
	if (floating.value && avContainer.value) {
		const m = getElementMeasure(avContainer.value);

		ms.containerWidth = m.width;
		ms.containerHeight = m.height;

		const floaterWidth = (ms.containerWidth + 2 * PB);
		const floaterHeight = (ms.containerHeight + 2 * PB);
		ms.floaterLeft = m.viewLeft - PB;
		ms.floaterTop = m.viewTop - PB;
		if ((ms.floaterLeft + floaterWidth) > screenSize.value.width) ms.floaterLeft = screenSize.value.width - floaterWidth;
		if ((ms.floaterTop + floaterHeight) > screenSize.value.height) ms.floaterTop = screenSize.value.height - floaterHeight;

		if (ms.floaterLeft < 0) ms.floaterLeft = 0;
		if (ms.floaterTop < 0) ms.floaterTop = 0;
	}
	return ms;
});


const containerStyle = computed<any>(() => {
	if (floating.value) {
		const s: CSSProperties = {};
		s['width'] = measure.value.containerWidth + "px";
		s['height'] = measure.value.containerHeight + "px";
		return [style.value, s];
	}
	return style.value;
});

const PB = 9; // padding 8, border 1

function isHorizontalScrollable() {
	return document.documentElement.scrollWidth > document.documentElement.clientWidth;
}

const floaterStyle = computed<CSSProperties>(() => {
	const s: CSSProperties = {};
	if (floating.value) {
		s['width'] = Math.ceil(measure.value.containerWidth + 2 * PB) + "px";
		s['height'] = Math.ceil(measure.value.containerHeight + 2 * PB) + "px";

		const extraSpaceForScrollbar = isHorizontalScrollable() ? 20 : 0;
		s['left'] = measure.value.floaterLeft + "px";
		s['top'] = measure.value.floaterTop - extraSpaceForScrollbar + "px";
	}
	return s;
});
const floaterClass = computed(() => {
	const c: string[] = [];
	if (floating.value) c.push("fd-floating");
	return c;
});

let observer = null;

function observe(entries: IntersectionObserverEntry[], observer: IntersectionObserver): void {
	entries.forEach((entry) => {
		if (entry.isIntersecting) {
			floating.value = false;
		} else {
			floating.value = true;
		}
	});
}

onMounted(() => {
	if (avContainer.value) {
		observer = new IntersectionObserver(observe, {
			threshold: 0
		});
		observer.observe(avContainer.value);
	}
});
</script>

<style>
.fd-always-visible {
}

.fd-floating {
	background-color: var(--bg);
	position: fixed;
	border: 1px solid var(--border-color);
	border-radius: 8px;
	padding: 8px;
	box-sizing: border-box;
	z-index: var(--drop-z-index);
	box-shadow: var(--panel-shadow);
}
</style>
